import dayjs from 'dayjs';
import { NotificationHlp } from 'src/lib/fliko/helpers/notification.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { TargetAddress } from 'src/lib/fliko/models/messageQueue/notification.data';
import { NewsData, NewsNotificationType, NewsReceiversData, NewsType } from 'src/lib/fliko/models/news/news.data';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';

export class NewsHelper {
  public static location(news: NewsData, ls: LocalizationService): string {
    let ret = '';
    const t = (x: string) => StrHlp.capitalize(ls.translate(x));
    if (news.receivers) {
      const r = news.receivers;
      if (r.apartments) {
        ret =
          r.apartments.length == 1
            ? `${t('commApartment.nominative')}: ` + r.apartments[0]
            : `${t('commApartment.plural')}: ` + r.apartments.join(', ');
      }
      if (r.staircases) {
        ret =
          r.staircases.length == 1
            ? `${t('label.staircase')}: ` + r.staircases[0]
            : `${t('label.staircases')}: ` + r.staircases.join(', ');
      }
      if (r.blocks) {
        ret = r.blocks.length == 1 ? `${t('addr.building')}: ` + r.blocks[0] : `${t('addr.buildings')}: ` + r.blocks.join(', ');
      }
    }

    if (!ret) {
      ret = t('select.allM');
    }
    return ret;
  }

  /**
   * Returns news duration as formatted and localized html output
   * @param {NewsData}            news
   * @param {LocalizationService} ls          localizationService
   * @param {boolean}             meetingTime if true, meeting time will be shown (or dateFrom/To if not exist)
   * @return formatted string, potentially with <br/> tag
   */
  public static duration(news: NewsData, ls: LocalizationService, meetingTime = false): string {
    const isMeeting = meetingTime && news.type == NewsType.MEETING;
    const f = isMeeting ? (news.meetingFrom ?? news.dateFrom) : news.dateFrom;
    const t = isMeeting ? (news.meetingTo ?? news.dateTo) : news.dateTo;
    const lh = new LocalizationHelper(ls);

    const date = (x: number) => lh.localizeDate(x, 'dMed');
    const time = (x: number) => lh.localizeDate(x, 'tShort');

    const from = ls.translate('label.from');
    const to = ls.translate('label.to');

    const sameDay = dayjs(f).endOf('day').isSame(dayjs(t).endOf('day'));

    if (meetingTime) {
      return sameDay
        ? `${date(f)} ${from} ${time(f)} ${to} ${time(t)}`
        : `${from} ${date(f)} ${time(f)} ${to} ${date(t)} ${time(t)}`;
    } else {
      return sameDay ? date(f) : `${from} ${date(f)} <br/> ${to} ${date(t)}`;
    }
  }

  public static getDefaultNotificationTypes(): Set<NewsNotificationType> {
    return new Set([NewsNotificationType.EMAIL, NewsNotificationType.MOBILE]);
  }

  /**
   * Reads notification type from legacy or current news
   * @param data
   * @return Set of NewsNotificationType
   */
  public static getNotificationTypes(data: NewsData): Set<NewsNotificationType> {
    return data.notifications
      ? new Set(Object.keys(data.notifications) as NewsNotificationType[])
      : new Set(data.sentNotificationTypes);
  }

  public static receiversToAddresses(rec: NewsReceiversData | null | undefined): TargetAddress[] {
    const ret: TargetAddress[] = [];
    if (rec) {
      if (rec.blocks) ret.push(...NotificationHlp.mapStringAddressToTargetAddress(rec.blocks));
      if (rec.staircases) ret.push(...NotificationHlp.mapStringAddressToTargetAddress(rec.staircases));
      if (rec.apartments) ret.push(...NotificationHlp.mapStringAddressToTargetAddress(rec.apartments));
    }
    return ret;
  }
}
